import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { Plus, Minus, Maximize } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  FIT_PADDING_TOP,
  FIT_PADDING_LEFT,
  FIT_PADDING_RIGHT,
  FIT_PADDING_BOTTOM,
  MIN_ITEM_Y,
} from '@/lib/canvasConstants';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;
const ZOOM_INCREMENT = 0.1; // 10% increments
const AUTO_HIDE_DELAY = 1500; // ms before notification hides

// Zoom notification pill (centered, auto-hides)
export function ZoomNotification() {
  const { zoom, viewMode } = useCanvasStore();
  const [isVisible, setIsVisible] = useState(false);
  const [displayZoom, setDisplayZoom] = useState(zoom);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastZoomRef = useRef(zoom);
  
  const percentage = Math.round(displayZoom * 100);
  
  useEffect(() => {
    if (zoom === lastZoomRef.current) return;
    lastZoomRef.current = zoom;
    
    setIsVisible(true);
    setDisplayZoom(zoom);
    
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, AUTO_HIDE_DELAY);
    
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [zoom]);
  
  if (viewMode !== 'focus') return null;

  return (
    <div 
      className={cn(
        'fixed bottom-24 left-1/2 -translate-x-1/2 z-40',
        'transition-all duration-300',
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-2 pointer-events-none'
      )}
    >
      <div className="px-4 py-2 rounded-full bg-background/95 backdrop-blur-sm border border-border shadow-lg">
        <span className="text-sm font-medium text-foreground">{percentage}%</span>
      </div>
    </div>
  );
}

// Zoom controls (+/- buttons with fit-to-view) - positioned in bottom-right, aligned with toolbar
export function ZoomControls() {
  const { zoom, setZoom, setPan, viewMode, currentBoardId, items } = useCanvasStore();
  
  // Zoom centered on the content centroid — keeps items visually stable
  // when using the +/- buttons.  Falls back to the usable-area center
  // (accounting for toolbar/sidebar padding) when there are no items so the
  // view doesn't drift toward the raw viewport center, which is offset from
  // where items actually appear.
  const zoomCentered = useCallback((newZoom: number) => {
    const state = useCanvasStore.getState();
    const { panX, panY, getBoundingBox } = state;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Try to use the content centroid as the focal point
    const bbox = getBoundingBox();
    let focalCanvasX: number;
    let focalCanvasY: number;

    if (bbox) {
      focalCanvasX = bbox.centerX;
      focalCanvasY = bbox.centerY;
    } else {
      // No items — use the usable-area center (asymmetric padding)
      const usableCenterX = FIT_PADDING_LEFT + (viewportWidth - FIT_PADDING_LEFT - FIT_PADDING_RIGHT) / 2;
      const usableCenterY = FIT_PADDING_TOP + (viewportHeight - FIT_PADDING_TOP - FIT_PADDING_BOTTOM) / 2;
      focalCanvasX = usableCenterX / zoom - panX;
      focalCanvasY = usableCenterY / zoom - panY;
    }

    // Where on screen is this focal canvas point currently?
    const focalScreenX = (focalCanvasX + panX) * zoom;
    const focalScreenY = (focalCanvasY + panY) * zoom;

    // Recalculate pan so the same canvas point stays at the same screen position
    const newPanX = focalScreenX / newZoom - focalCanvasX;
    const newPanY = focalScreenY / newZoom - focalCanvasY;

    setZoom(newZoom);
    setPan(newPanX, newPanY);
  }, [zoom, setZoom, setPan]);

  const zoomIn = () => {
    const currentPercent = Math.round(zoom * 100);
    const newPercent = Math.min(currentPercent + 10, ZOOM_MAX * 100);
    zoomCentered(newPercent / 100);
  };

  const zoomOut = () => {
    const currentPercent = Math.round(zoom * 100);
    const newPercent = Math.max(currentPercent - 10, ZOOM_MIN * 100);
    zoomCentered(newPercent / 100);
  };

  const fitToView = useCallback(() => {
    const { getBoundingBox } = useCanvasStore.getState();
    const bounds = getBoundingBox();

    if (!bounds) {
      setPan(0, 0);
      setZoom(1);
      return;
    }

    // Match InfiniteCanvas.fitAllItemsInView: account for UI overlays
    const safeBounds = {
      ...bounds,
      minY: Math.max(bounds.minY, MIN_ITEM_Y),
      centerY: bounds.minY < MIN_ITEM_Y
        ? (MIN_ITEM_Y + bounds.maxY) / 2
        : bounds.centerY,
    };

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const usableWidth = viewportWidth - FIT_PADDING_LEFT - FIT_PADDING_RIGHT;
    const usableHeight = viewportHeight - FIT_PADDING_TOP - FIT_PADDING_BOTTOM;

    const scaleX = usableWidth / safeBounds.width;
    const scaleY = usableHeight / (safeBounds.maxY - safeBounds.minY);
    const newZoom = Math.min(Math.max(Math.min(scaleX, scaleY), ZOOM_MIN), 2);

    const usableCenterX = FIT_PADDING_LEFT + usableWidth / 2;
    const usableCenterY = FIT_PADDING_TOP + usableHeight / 2;

    setZoom(newZoom);
    setPan(usableCenterX / newZoom - safeBounds.centerX, usableCenterY / newZoom - safeBounds.centerY);
  }, [setZoom, setPan]);
  
  const isAtMin = Math.round(zoom * 100) <= ZOOM_MIN * 100;
  const isAtMax = Math.round(zoom * 100) >= ZOOM_MAX * 100;
  
  if (viewMode !== 'focus' || !currentBoardId) return null;

  return (
    <div className="fixed bottom-6 right-4 z-40 flex flex-col gap-2">
      {/* Fit to view button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={fitToView}
            className={cn(
              'p-2.5 rounded-xl shadow-lg border transition-all duration-200',
              'hover:scale-105 active:scale-95',
              'flex items-center justify-center',
              'bg-background/95 backdrop-blur-sm text-muted-foreground border-border hover:bg-muted hover:text-foreground'
            )}
            title="Fit all items to view"
          >
            <Maximize className="h-5 w-5" strokeWidth={2} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>Fit to view</p>
        </TooltipContent>
      </Tooltip>

      {/* Zoom controls */}
      <div className="bg-background/95 backdrop-blur-sm rounded-xl border border-border shadow-lg p-1">
        <button
          onClick={zoomIn}
          disabled={isAtMax}
          className={cn(
            'p-2 rounded-lg transition-colors',
            'hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed',
            'text-foreground'
          )}
          title="Zoom in (+10%)"
        >
          <Plus className="h-5 w-5" strokeWidth={2} />
        </button>
        
        <div className="h-px bg-border mx-1" />
        
        <button
          onClick={zoomOut}
          disabled={isAtMin}
          className={cn(
            'p-2 rounded-lg transition-colors',
            'hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed',
            'text-foreground'
          )}
          title="Zoom out (-10%)"
        >
          <Minus className="h-5 w-5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
